import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { sessionManager, SessionState } from '@/lib/session/SessionManager'

export interface UseSessionReturn {
  sessionState: SessionState
  timeRemainingMinutes: number
  isSessionActive: boolean
  extendSession: () => void
  forceLogout: () => void
  showWarning: boolean
}

export function useSession(): UseSessionReturn {
  const [sessionState, setSessionState] = useState<SessionState>(sessionManager.getState())
  const [showWarning, setShowWarning] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, isAuthenticated } = useAuthStore()
  
  // Check if we're on an authentication page (login, 2FA, forgot password, etc.)
  const isAuthPage = location.pathname.startsWith('/login') || 
                     location.pathname.startsWith('/two-factor') || 
                     location.pathname.startsWith('/forgot-password') ||
                     location.pathname.startsWith('/reset-password') ||
                     location.pathname === '/'

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    await logout()
    navigate('/login?reason=timeout')
  }, [logout, navigate])

  // Handle session warning
  const handleWarning = useCallback((timeRemaining: number) => {
    setShowWarning(true)
    setSessionState(sessionManager.getState())
  }, [])

  // Extend session
  const extendSession = useCallback(() => {
    sessionManager.extendSession()
    setShowWarning(false)
    setSessionState(sessionManager.getState())
  }, [])

  // Force logout
  const forceLogout = useCallback(() => {
    sessionManager.forceLogout()
    handleTimeout().catch((error) => {
      console.error('[useSession] Failed to handle timeout logout:', error)
      navigate('/login?reason=timeout')
    })
  }, [handleTimeout, navigate])

  // Initialize session manager with defaults (no backend security-settings call)
  useEffect(() => {
    // Don't initialize session timeout on auth pages or if not authenticated
    if (isAuthPage || !isAuthenticated) {
      sessionManager.destroy()
      setShowWarning(false)
      return
    }

    sessionManager.destroy()
    setShowWarning(false)

    sessionManager.updateConfig({
      enabled: true,
      timeoutMinutes: 30
    })
    sessionManager.setCallbacks({
      onWarning: handleWarning,
      onTimeout: handleTimeout
    })
    sessionManager.initialize()

    // Update state periodically
    const interval = setInterval(() => {
      setSessionState(sessionManager.getState())
    }, 1000)

    return () => {
      clearInterval(interval)
      sessionManager.destroy()
    }
  }, [handleWarning, handleTimeout, isAuthPage, isAuthenticated])

  return {
    sessionState,
    timeRemainingMinutes: sessionManager.getTimeRemainingMinutes(),
    isSessionActive: sessionManager.isSessionActive(),
    extendSession,
    forceLogout,
    showWarning
  }
}

